import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaVenusMars,
  FaBirthdayCake,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaArrowLeft,
  FaCamera,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ForgotPasswordNewPage from "../../Components/User/ForgotPasswordNewPage";
// import { useUser } from "../../Context/UserContext";
import { updateProfileImageInStorage } from "../../utils/profileImageUtils";
const api = () =>
  axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

export default function ProfilePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // const { setProfileImage } = useUser();
  const [user, setUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const fetchProfile = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      setProfileLoading(true);
      const { data } = await api().get("/users/profile");
      setUser(data);
      updateProfileImageInStorage(data.profileImage);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      } else {
        toast.error("Failed to load profile");
      }
    } finally {
      setProfileLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Add this helper function above handleFileChange in ProfilePage.jsx
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const img = document.createElement("img");
      const url = URL.createObjectURL(file);

      img.onload = () => {
        // Max 400x400 for profile picture
        const MAX = 400;
        let { width, height } = img;

        if (width > height) {
          if (width > MAX) {
            height = (height * MAX) / width;
            width = MAX;
          }
        } else {
          if (height > MAX) {
            width = (width * MAX) / height;
            height = MAX;
          }
        }

        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url); // cleanup memory
            resolve(new File([blob], file.name, { type: "image/webp" }));
          },
          "image/webp", // webp is smallest format
          0.8, // 80% quality — good balance
        );
      };

      img.src = url;
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB");
      return;
    }

    try {
      setUploadingImage(true);

      // ← Compress before uploading
      const compressed = await compressImage(file);
      console.log(
        `Original: ${(file.size / 1024).toFixed(1)}KB → Compressed: ${(compressed.size / 1024).toFixed(1)}KB`,
      );

      const formData = new FormData();
      formData.append("file", compressed); // ← use compressed
      formData.append(
        "upload_preset",
        process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET,
      );

      const cloudinaryRes = await axios.post(
        process.env.REACT_APP_CLOUDINARY_UPLOAD_URL,
        formData,
      );

      const imageUrl = cloudinaryRes.data.secure_url;

      const { data } = await api().post("/users/update-my-profile", {
        imageUrl,
      });
      setUser(data.user);
      updateProfileImageInStorage(data.user.profileImage);
      toast.success("Profile photo updated!");
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      setPwdLoading(true);
      await api().put("/users/update-password", {
        currentPassword,
        newPassword,
      });
      toast.success("Password updated successfully");
      setShowPasswordReset(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update password");
    } finally {
      setPwdLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 text-sm font-medium">Loading profile…</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="w-full">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-600 mb-6 hover:text-blue-800 transition-colors"
        >
          <FaArrowLeft className="mr-2" /> Back
        </button>

        <div className="bg-white shadow rounded-2xl overflow-hidden w-full">
          {/* Header */}
          <div className="bg-blue-600 px-6 py-8 text-start">
            <div className="inline-flex flex-col items-center gap-2">
              <div className="relative group">
                <img
                  className="h-24 w-24 rounded-full border-4 border-white object-cover shadow-lg"
                  src={user.profileImage || "https://via.placeholder.com/150"}
                  alt={user.name}
                />
                <span className="absolute bottom-1 right-1 bg-green-400 rounded-full h-5 w-5 border-2 border-white" />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-full transition-all duration-200 cursor-pointer disabled:cursor-wait"
                >
                  {uploadingImage ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin opacity-0 group-hover:opacity-100 transition-opacity" />
                  ) : (
                    <FaCamera className="text-white text-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </button>
              </div>

              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm px-3 py-1 rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-wait"
              >
                {uploadingImage ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Uploading…
                  </>
                ) : (
                  <>
                    <FaCamera className="text-xs" />
                    Change Photo
                  </>
                )}
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            <h1 className="mt-4 text-2xl font-bold text-white">
              {user.name || "User"}
            </h1>
            <p className="text-blue-100 capitalize">{user.role || "User"}</p>
            {user.entityId && (
              <p className="text-blue-200 text-sm mt-0.5 font-mono">
                {user.entityId}
              </p>
            )}
          </div>

          {/* Details */}
          <div className="px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-5">
                <InfoRow
                  icon={<FaUser />}
                  label="Full Name"
                  value={user.name}
                />
                <InfoRow
                  icon={<FaEnvelope />}
                  label="Email Address"
                  value={user.email}
                />
                <InfoRow
                  icon={<FaPhone />}
                  label="Phone Number"
                  value={user.phone}
                />
              </div>
              <div className="space-y-5">
                <InfoRow
                  icon={<FaVenusMars />}
                  label="Gender"
                  value={
                    user.gender === "male"
                      ? "Male"
                      : user.gender === "female"
                        ? "Female"
                        : "Other"
                  }
                />
                <InfoRow
                  icon={<FaBirthdayCake />}
                  label="Age"
                  value={user.age ? `${user.age} years` : undefined}
                />
                <InfoRow
                  icon={<FaLock />}
                  label="Account Created"
                  value={formatDate(user.createdAt)}
                />
              </div>
            </div>

            {/* <div className="mt-5">
              <span
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full capitalize ${user.status === "active" ? "bg-green-100 text-green-700" : user.status === "banned" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${user.status === "active" ? "bg-green-500" : user.status === "banned" ? "bg-red-500" : "bg-gray-400"}`}
                />
                {user.status || "active"}
              </span>
            </div> */}

            <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col sm:flex-row sm:justify-start gap-3">
              <button
                onClick={() => setShowPasswordReset(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-xl transition-colors text-sm"
              >
                Change Password
              </button>
              <button
                onClick={() => setShowForgotPassword(true)}
                className="text-blue-600 hover:text-blue-800 font-semibold py-2 px-6 rounded-xl transition-colors text-sm border border-blue-200 hover:border-blue-400"
              >
                Reset Password?
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-5 text-gray-800">
                Change Password
              </h2>
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <PasswordField
                  id="currentPassword"
                  label="Current Password"
                  value={currentPassword}
                  onChange={setCurrentPassword}
                  show={showCurrentPwd}
                  onToggle={() => setShowCurrentPwd((v) => !v)}
                />
                <PasswordField
                  id="newPassword"
                  label="New Password"
                  value={newPassword}
                  onChange={setNewPassword}
                  show={showNewPwd}
                  onToggle={() => setShowNewPwd((v) => !v)}
                />
                <PasswordField
                  id="confirmPassword"
                  label="Confirm New Password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  show={showConfirmPwd}
                  onToggle={() => setShowConfirmPwd((v) => !v)}
                />
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowPasswordReset(false)}
                    className="px-4 py-2 border rounded-xl text-gray-700 hover:bg-gray-50 text-sm font-medium"
                    disabled={pwdLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={pwdLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 text-sm font-semibold flex items-center gap-2"
                  >
                    {pwdLoading && (
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                    {pwdLoading ? "Updating…" : "Update Password"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <ForgotPasswordNewPage
              onClose={() => setShowForgotPassword(false)}
              userEmail={user.email}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-blue-500 mt-0.5 flex-shrink-0">{icon}</span>
      <div>
        <p className="text-xs text-gray-400 font-medium">{label}</p>
        <p className="font-semibold text-gray-800 text-sm mt-0.5">
          {value || "N/A"}
        </p>
      </div>
    </div>
  );
}

function PasswordField({ id, label, value, onChange, show, onToggle }) {
  return (
    <div>
      <label
        className="block text-sm font-medium text-gray-700 mb-1"
        htmlFor={id}
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? "text" : "password"}
          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
        />
        <button
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          onClick={onToggle}
        >
          {show ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
        </button>
      </div>
    </div>
  );
}
